import React from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const inputCls = "w-full px-3 py-2 bg-black/40 border border-white/10 focus:border-[#d4a017] outline-none text-sm text-white rounded-sm transition-colors";

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-white/40">{hint}</span>}
  </label>
);

export const Choice = ({ value, onChange, options, testId, disabled }) => (
  <select data-testid={testId} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
    className={`${inputCls} disabled:opacity-50`}>
    {options.map((o) => <option key={o} value={o} className="bg-[#0B0F19]">{o.replace(/_/g, " ")}</option>)}
  </select>
);

const TONES = {
  active: "bg-emerald-500/15 text-emerald-300", disabled: "bg-red-500/15 text-red-300",
  inactive: "bg-red-500/15 text-red-300", pending: "bg-amber-500/15 text-amber-300",
  super_admin: "bg-[#d4a017]/20 text-[#d4a017]", partner: "bg-sky-500/15 text-sky-300", staff: "bg-white/10 text-white/70",
};
export const Pill = ({ v, testId }) => (
  <span data-testid={testId} className={`font-mono text-[10px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-sm ${TONES[v] || "bg-white/10 text-white/70"}`}>
    {(v || "—").replace(/_/g, " ")}
  </span>
);

export const PermGrid = ({ catalog, value, onToggle, testPrefix, disabled }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {Object.entries(catalog).map(([key, label]) => {
      const on = value[key];
      return (
        <button type="button" key={key} disabled={disabled} data-testid={`${testPrefix}-${key}`} onClick={() => onToggle(key)}
          className={`text-left p-2.5 border rounded-sm transition-colors disabled:opacity-50 ${on ? "border-[#d4a017]/70 bg-[#d4a017]/10" : "border-white/10 hover:border-white/30"}`}>
          <div className="flex items-center gap-2">
            <span className={`h-3.5 w-3.5 border rounded-[2px] grid place-items-center ${on ? "bg-[#d4a017] border-[#d4a017]" : "border-white/30"}`}>
              {on && <span className="h-1.5 w-1.5 bg-black" />}
            </span>
            <span className="font-mono text-[11px] text-white">{key}</span>
          </div>
          <div className="text-[11px] text-white/50 mt-1 pl-5.5">{typeof label === "string" ? label : label.label}</div>
        </button>
      );
    })}
  </div>
);

export const SecretBox = ({ label, value, testId }) => (
  <div data-testid={testId} className="p-3 border border-[#d4a017]/50 bg-[#d4a017]/5 rounded-sm space-y-2">
    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#d4a017]">{label} · shown once</div>
    <div className="flex items-center gap-2">
      <code className="flex-1 text-xs text-white break-all">{value}</code>
      <button type="button" data-testid={`${testId}-copy`} onClick={() => navigator.clipboard.writeText(value).then(() => toast.success("Copied"))}
        className="p-2 border border-white/10 hover:border-[#d4a017] text-white/70 hover:text-[#d4a017]"><Copy size={13} /></button>
    </div>
  </div>
);

export const Modal = ({ open, onClose, title, children, testId }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid={testId} className="bg-[#0F1422] border border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="serif-head text-xl text-white">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export const Btn = ({ children, tone = "gold", className = "", ...p }) => (
  <button type="button" {...p}
    className={`inline-flex items-center gap-1.5 px-3 py-2 border font-mono text-[10px] tracking-[0.18em] uppercase rounded-sm transition-colors disabled:opacity-50 ${tone === "gold" ? "border-[#d4a017] text-[#d4a017] hover:bg-[#d4a017]/10" : tone === "red" ? "border-red-500/50 text-red-300 hover:bg-red-500/10" : "border-white/15 text-white/70 hover:border-white/40"} ${className}`}>
    {children}
  </button>
);

export const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
